import { useState } from "react";
import { Sample } from "@/comn/components/_";
import { Group, Layout, FormControl, Button, Grid, Text, Icon, CommonErrors } from "@/comn/components";
import { useForm, TFormSchema, useResource, useModal, useGrid, TGridSchema } from "@/comn/hooks";
import "prismjs/themes/prism.css";
import { comnEnvs, comnUtils } from "@/comn/utils";
import { Link } from "react-router-dom";

const sample = ({ totalElements = 99 }) => {
    return {
        page: { totalElements, page: 0, size: 100 },
        content: Array(totalElements)
            .fill(null)
            .map((_) => ({
                group: ["Group 1", "Group 2", "Group 3", "Group 4"][Math.floor(Math.random() * 4)],
                subGroup: ["Sub Group 1", "Sub Group 2", "Sub Group 3", "Sub Group 4"][Math.floor(Math.random() * 4)],
                number1: Math.ceil(Math.random() * 1000),
                number2: Math.ceil(Math.random() * 1000),
                number3: Math.ceil(Math.random() * 1000),
                number4: Math.ceil(Math.random() * 1000),
                datetime: "2022-10-10 10:30:20",
            })),
    };
};

const validateSample = ({ totalElements = 99 }) => {
    return {
        page: { totalElements: 10, page: 0, size: 100 },
        content: [{}, {}, {}, {}, {}],
    };
};

const GRID_SCHEMA: TGridSchema = {
    options: {
        height: 500,
        index: true,
        // 그룹으로 묶을 데이터의 필드명 정의(배열로 여러개 가능)
        group: ["group"],
    },
    // 그룹으로 묶는 데이터의 제일 상단에 정보 표시하는 Row 설정
    group: [
        { cells: [{ colspan: 2, text: "그룹의 제일 상단 Row" }] },
        //
        { cells: [{ binding: "number1", aggregate: "SUM", align: "right" }] }, // SUM : 합계
        { cells: [{ binding: "number2", aggregate: "AVERAGE", align: "right" }] }, // AVERAGE : 평균
        { cells: [{ binding: "number3", aggregate: "COUNT", align: "right" }] }, // COUNT : 갯수
        { cells: [{ binding: "number4", aggregate: "MAX", align: "right" }] }, // MAX : 최대
        { cells: [{ binding: "number4", aggregate: "MIN", align: "right" }] }, // MIN : 최소
    ],
    // 그룹으로 묶는 데이터의 제일 하단에 정보 표시하는 Row 설정
    groupFoot: [
        { cells: [{ colspan: 2, text: "그룹의 제일 하단 Row" }] },
        { cells: [{ binding: "number1", aggregate: "SUM", align: "right" }] }, // SUM : 합계
        { cells: [{ binding: "number2", aggregate: "AVERAGE", align: "right" }] }, // AVERAGE : 평균
        { cells: [{ binding: "number3", aggregate: "COUNT", align: "right" }] }, // COUNT : 갯수
        { cells: [{ binding: "number4", aggregate: "MAX", align: "right" }] }, // MAX : 최대
        { cells: [{ binding: "number4", aggregate: "MIN", align: "right" }] }, // MIN : 최소
    ],
    head: [
        { cells: [{ binding: "group", width: 200 }] },
        { cells: [{ binding: "subGroup", width: 200 }] },
        { cells: [{ binding: "number1", width: 200 }] },
        { cells: [{ binding: "number2", width: 200 }] },
        { cells: [{ binding: "number3", width: 200 }] },
        { cells: [{ binding: "number4", width: 200 }] },
        { cells: [{ binding: "datetime", width: 50 }] },
    ],
    body: [
        { cells: [{ binding: "group" }] },
        { cells: [{ binding: "subGroup" }] },
        { cells: [{ binding: "number1" }] },
        { cells: [{ binding: "number2" }] },
        { cells: [{ binding: "number3" }] },
        { cells: [{ binding: "number4" }] },
        { cells: [{ binding: "datetime" }] },
    ],
};

const SG_GUIDE_LIST: TGridSchema = {
    id: "grid",
    options: {
        radio: true,
        checkbox: true,
        pagination: "in",
        index: true,
        edit: false,
        height: 200,
    },
    head: [
        { cells: [{ header: "Group", binding: "group", width: 200 }] },
        { cells: [{ header: "Sub Group", binding: "subGroup", width: 200 }] },
        { cells: [{ header: "Number 1", binding: "number1", width: 150 }] },
        { cells: [{ header: "Number 2", binding: "number2", width: 150 }] },
        { cells: [{ header: "Datetime", binding: "datetime", width: "*" }] },
    ],
    body: [
        { cells: [{ binding: "group" }] },
        { cells: [{ binding: "subGroup" }] },
        { cells: [{ binding: "number1", align: "right", type: "number", thousandSeparator: true }] },
        { cells: [{ binding: "number2", align: "right", type: "number", thousandSeparator: true }] },
        { cells: [{ binding: "datetime" }] },
    ],
};

export const SG_VALIDATE_LIST: TGridSchema = {
    id: "grid",
    options: {
        radio: true,
        pagination: "in",
        index: true,
        edit: true,
    },
    head: [
        {
            cells: [
                {
                    header: "L_DCLR_NO",
                    binding: "dclrNo",
                    required: true,
                    width: 300,
                },
            ],
        },
        { cells: [{ header: "L_WRHS", binding: "wrhsCd", width: 200 }], id: "wrhsCd" },
        { cells: [{ header: "L_MRN", binding: "mrn", width: 200 }], id: "mrn" },
        {
            cells: [
                {
                    header: "L_MSN",
                    binding: "msn",
                    width: 200,
                },
            ],
        },
        { cells: [{ header: "L_MBL_NO", binding: "mblNo", width: 200 }] },
        { cells: [{ header: "L_GODS_DESC", binding: "godsDesc", width: "3*" }] },
        { cells: [{ header: "L_PCKG_NO", binding: "blPckgNo", width: 200 }] },
        { cells: [{ header: "L_GWGHT", binding: "blGwght", width: 200 }] },
        { cells: [{ header: "L_PRCSS_STAT", binding: "prcssStatCd", width: 200 }] },
        { cells: [{ header: "L_FRST_RGSR_DTM", binding: "frstRgsrDtm", width: 200 }] },
    ],
    body: [
        {
            cells: [
                {
                    binding: `dclrNo`,
                    required: true,
                    minLength: 5,
                    pattern: /^[0-9a-zA-Z]([-_.]?[0-9a-zA-Z])*@[0-9a-zA-Z]([-_.]?[0-9a-zA-Z])*.[a-zA-Z]{2,3}$/i,
                },
            ],
        },
        {
            cells: [{ binding: "wrhsCd", type: "code", area: "wrhsCd", viewType: "value", required: true }],
        },
        {
            cells: [{ binding: "mrn", rightText: " KG", required: true }],
        },

        {
            cells: [{ binding: "msn", required: true }],
        },
        {
            cells: [
                {
                    binding: "mblNo",
                    required: true,
                    validate: (value: any) => {
                        // 값(value)을 비교하여 return true(오류검증 통과) , 이외의 경우 오류검증 실패 처리
                        if (value !== "111") return true;
                    },
                },
            ],
        },
        {
            cells: [{ binding: "godsDesc", required: true }],
        },
        {
            cells: [
                {
                    binding: "blPckgNo",
                    align: "right",
                    type: "number",
                    thousandSeparator: true,
                    required: true,
                    max: 1000,
                },
            ],
        },
        {
            cells: [
                {
                    binding: "blGwght",
                    align: "right",
                    type: "number",
                    thousandSeparator: true,
                    required: true,
                    min: 200,
                },
            ],
        },
        {
            cells: [
                {
                    binding: "prcssStatCd",
                    type: "select",
                    required: true,
                    area: "comnCd",
                    comnCd: "OSC0008",
                    viewType: "both",
                },
            ],
        },
        {
            cells: [{ binding: "frstRgsrDtm", type: "datetime", required: true }],
        },
    ],
};

export const GuideRouter = () => {
    useResource({
        defaultSchema: [{ area: "comnCd", comnCd: "OSC0008" }, { area: "wrhsCd" }],
    });
    const modal = useModal(); // Modal Window Hook !== Modal 창 Hook ==!

    const grid = {
        rpckItmAppList: useGrid({
            defaultSchema: SG_GUIDE_LIST,
            page: 0,
            size: 10,
        }),
        rpckItmAppListUpDown: useGrid({
            defaultSchema: SG_GUIDE_LIST,
            page: 0,
            size: 10,
        }),
        rpckItmAppListSchema: useGrid({
            defaultSchema: SG_GUIDE_LIST,
            page: 0,
            size: 10,
        }),
        rpckItmAppListSum: useGrid({
            defaultSchema: GRID_SCHEMA,
            page: 0,
            size: 10,
        }),
        rpckItmAppListValidate: useGrid({
            defaultSchema: SG_VALIDATE_LIST,
            page: 0,
            size: 10,
        }),
    };

    return (
        <Sample title="Front-End 업데이트(2024.05.01)" description="">
            <Sample.Section
                title="1. 그리드 엑셀 다운로드 기능"
                description={
                    <>
                        - 그리드에 조회된 데이터를 엑셀로 다운로드( 1,000개 데이터 다운로드시 그리드의 페이지 사이즈를
                        1,000으로 변경 및 조회 후 다운로드 )<br />- Maximum 1,000 개 데이터 다운로드
                        <br />- useGrid Hook의 exportExcel 메소드 사용
                        <br />- exportExcel({`{ excelName , sheetName}`})
                    </>
                }
            >
                <Sample.Table
                    widths={["w-[15%]", "w-[15%]", "w-[15%]", "w-[30%]", "w-auto"]}
                    data={[
                        ["파라메터", "타입", "필수여부", "기본값", "비고"],
                        ["excelName", "string", "N", "Export(YYYY-MM-DD HH:mm:ss).xlsx", "확장자를 제외한 엑셀 파일명"],
                        ["sheetName", "string", "N", "Sheet", "엑셀파일 시트명"],
                    ]}
                />
                <Sample.Code>{`<Button
    role="excelDown"
    onClick={() => {
        grid.rpckItmAppList.exportExcel({
            /* 다운로드 엑셀 파일명 (확장자 없이), 기본값 Export(년월일시분초) */
            excelName: "Excel File Name",
            /* 다운로드 엑셀 시트명 , 기본값 Sheet */
            sheetName: "Sheet Name",
        });
    }}
></Button>`}</Sample.Code>
                <Layout direction="row">
                    <Layout.Right>
                        <Button
                            role="excelDown"
                            onClick={() => {
                                grid.rpckItmAppList.exportExcel({
                                    excelName: "Excel File Name",
                                    sheetName: "Sheet Name",
                                });
                            }}
                        ></Button>
                    </Layout.Right>
                </Layout>
                <Grid {...grid.rpckItmAppList.grid} data={sample({})} />
            </Sample.Section>
            <Sample.Section
                title="2. 그리드 행순서 변경 메소드 추가"
                description={
                    <>
                        - 그리드의 선택된 Row (radio:true)의 순서를 위 아래로 변경하는 기능
                        <br />
                        - getSelectedRow 메소드로 선택된 Row 전달(단일건)
                        <br />- useGrid Hook의 upRow, downRow 메소드 사용
                    </>
                }
            >
                <Sample.Table
                    widths={["w-[15%]", "w-[25%]", "w-auto"]}
                    data={[
                        ["메소드명", "파라메터", "비고"],
                        ["upRow", "Grid Row", "그리드의 선택된 Row를 위로 한줄 이동"],
                        ["downRow", "Grid Row", "그리드의 선택된 Row를 아래로 한줄이동"],
                    ]}
                />
                <Sample.Code>{`<Button
    icon="up"
    onClick={() => {
        // 선택된 그리드 Row를 위쪽으로 이동
        grid.rpckItmAppListUpDown.upRow(grid.rpckItmAppListUpDown.getSelectedRow());
    }}
></Button>
<Button
    icon="down"
    onClick={() => {
        // 선택된 그리드 Row를 아래쪽으로 이동
        grid.rpckItmAppListUpDown.downRow(grid.rpckItmAppListUpDown.getSelectedRow());
    }}
></Button>`}</Sample.Code>
                <Layout direction="row">
                    <Layout.Right>
                        <Button
                            icon="up"
                            onClick={() => {
                                grid.rpckItmAppListUpDown.upRow(grid.rpckItmAppListUpDown.getSelectedRow());
                            }}
                        ></Button>
                        <Button
                            icon="down"
                            onClick={() => {
                                grid.rpckItmAppListUpDown.downRow(grid.rpckItmAppListUpDown.getSelectedRow());
                            }}
                        ></Button>
                    </Layout.Right>
                </Layout>
                <Grid {...grid.rpckItmAppListUpDown.grid} data={sample({})} />
            </Sample.Section>
            <Sample.Section
                title="3. 그리드 동적 스키마 적용 기능 추가"
                description={
                    <>
                        - 그리드의 스키마(head, body) 동적변경 기능
                        <br />- useGrid Hook의 setHead, setBody, setSchema 메소드 사용
                    </>
                }
            >
                <Sample.Table
                    widths={["w-[15%]", "w-[25%]", "w-auto"]}
                    data={[
                        ["메소드명", "파라메터", "비고"],
                        ["setHead", "Array : 변경할 스키마(TGridSchema.head)", "grid.그리드명.setHead( [...] )"],
                        ["setBody", "Array : 변경할 스키마(TGridSchema.body)", "grid.그리드명.setBody( [...] )"],
                        ["setSchema", "TGridSchema : 변경할 스키마(TGridSchema)", "grid.그리드명.setSchema( {...} )"],
                    ]}
                />
                <Sample.Code>{`<Button
    onClick={() => {
        // head 스키마 변경        
        grid.rpckItmAppListSchema.setHead(
            // 변경할 그리드 스키마(head)
            [
            { cells: [{ header: "L_WRHS", binding: "wrhsCd", width: 200 }], id: "wrhsCd" },
            {
                cells: [
                    {
                        header: "L_MSN",
                        binding: "msn",
                        width: 200,
                    },
                ],
            },
            { cells: [{ header: "L_GODS_DESC", binding: "godsDesc", width: "3*" }] },
            { cells: [{ header: "L_GWGHT", binding: "blGwght", width: 200 }] },
            { cells: [{ header: "L_FRST_RGSR_DTM", binding: "frstRgsrDtm", width: 200 }] },
        ]);
    }}
>
    Change head schema
</Button>
<Button
    
    onClick={() => {        
        // body 스키마 변경        
        grid.rpckItmAppListSchema.setBody(
            // 변경할 그리드 스키마(body)
            [
            {
                cells: [{ binding: "wrhsCd", type: "code", area: "wrhsCd", viewType: "value" }],
            },
            {
                cells: [
                    {
                        binding: "msn",
                    },
                ],
            },
            {
                cells: [{ binding: "godsDesc" }],
            },
            {
                cells: [
                    {
                        binding: "blGwght",
                        align: "right",
                        type: "number",
                        thousandSeparator: true,
                    },
                ],
            },
            {
                cells: [{ binding: "frstRgsrDtm", type: "datetime", second: true }],
            },
        ]);
    }}
>
    Change body schema
</Button>
<Button
    onClick={() => {
        // 그리드 전체 스키마 변경
        grid.rpckItmAppListSchema.setSchema(
            // 변경할 그리드 스키마(전체)
            {
            id: "grid_change",
            options: {
                radio: true,
                pagination: "out",
                index: true,
                edit: false,
                height: "auto",
            },
            head: [
                {
                    cells: [
                        { header: "L_DCLR_NO", binding: "dclrNo", required: true, width: 300 },
                    ],
                },
                { cells: [{ header: "L_WRHS", binding: "wrhsCd", width: 200 }], id: "wrhsCd" },
                { cells: [{ header: "L_MRN", binding: "mrn", width: 200 }], id: "mrn" },
                {
                    cells: [
                        {
                            header: "L_MSN",
                            binding: "msn",
                            width: 200,
                        },
                    ],
                },
            ],
            body: [
                {
                    cells: [
                        {
                            binding: "dclrNo",
                        },
                    ],
                },
                {
                    cells: [
                        { binding: "wrhsCd", type: "code", area: "wrhsCd", viewType: "value" },
                    ],
                },
                {
                    cells: [{ binding: "mrn", rightText: " KG" }],
                },

                {
                    cells: [
                        {
                            binding: "msn",
                        },
                    ],
                },
            ],
        });
    }}
>
    Change Schema
</Button>`}</Sample.Code>
                <Layout direction="row">
                    <Layout.Right>
                        <Button
                            onClick={() => {
                                grid.rpckItmAppListSchema.setHead([
                                    { cells: [{ header: "L_WRHS", binding: "wrhsCd", width: 200 }], id: "wrhsCd" },
                                    {
                                        cells: [
                                            {
                                                header: "L_MSN",
                                                binding: "msn",
                                                width: 200,
                                            },
                                        ],
                                    },
                                    { cells: [{ header: "L_GODS_DESC", binding: "godsDesc", width: "3*" }] },
                                    { cells: [{ header: "L_GWGHT", binding: "blGwght", width: 200 }] },
                                    { cells: [{ header: "L_FRST_RGSR_DTM", binding: "frstRgsrDtm", width: 200 }] },
                                ]);
                            }}
                        >
                            Change head schema
                        </Button>
                        <Button
                            onClick={() => {
                                grid.rpckItmAppListSchema.setBody([
                                    {
                                        cells: [{ binding: "wrhsCd", type: "code", area: "wrhsCd", viewType: "value" }],
                                    },
                                    {
                                        cells: [
                                            {
                                                binding: "msn",
                                            },
                                        ],
                                    },
                                    {
                                        cells: [{ binding: "godsDesc" }],
                                    },
                                    {
                                        cells: [
                                            {
                                                binding: "blGwght",
                                                align: "right",
                                                type: "number",
                                                thousandSeparator: true,
                                            },
                                        ],
                                    },
                                    {
                                        cells: [{ binding: "frstRgsrDtm", type: "datetime", second: true }],
                                    },
                                ]);
                            }}
                        >
                            Change body schema
                        </Button>
                        <Button
                            onClick={() => {
                                grid.rpckItmAppListSchema.setSchema({
                                    id: "grid_change",
                                    options: {
                                        radio: true, //체크박스 보이기 숨기기, default : false
                                        pagination: "out", // 그리드 데이터 페이징 "out" : 데이터를 외부에서 페이징해서 가져오는 경우 , "in" : 데이터를 전체 가져와서 그리드 내부에서 페이징 및 편집
                                        index: true, // 그리드 번호  index : true ( asc )  , index : "DESC" ( desc 역순 )
                                        edit: false, // 그리드 편집 가능 여부 , default : false
                                        height: "auto",
                                    },
                                    head: [
                                        {
                                            cells: [
                                                { header: "L_DCLR_NO", binding: "dclrNo", required: true, width: 300 },
                                            ],
                                        },
                                        { cells: [{ header: "L_WRHS", binding: "wrhsCd", width: 200 }], id: "wrhsCd" },
                                        { cells: [{ header: "L_MRN", binding: "mrn", width: 200 }], id: "mrn" },
                                        {
                                            cells: [
                                                {
                                                    header: "L_MSN",
                                                    binding: "msn",
                                                    width: 200,
                                                },
                                            ],
                                        },
                                    ],
                                    body: [
                                        {
                                            cells: [
                                                {
                                                    binding: `dclrNo`,
                                                },
                                            ],
                                        },
                                        {
                                            cells: [
                                                { binding: "wrhsCd", type: "code", area: "wrhsCd", viewType: "value" },
                                            ],
                                        },
                                        {
                                            cells: [{ binding: "mrn", rightText: " KG" }],
                                        },

                                        {
                                            cells: [
                                                {
                                                    binding: "msn",
                                                },
                                            ],
                                        },
                                    ],
                                });
                            }}
                        >
                            Change Schema
                        </Button>
                    </Layout.Right>
                </Layout>
                <Grid {...grid.rpckItmAppListSchema.grid} data={sample({})} />
            </Sample.Section>
            <Sample.Section
                title="4. 그리드 그룹, 집계 기능 추가"
                description={
                    <>
                        - 그리드의 그룹(특정 필드를 기준으로 값이 같은 데이터를 묶음)기능 추가
                        <br />
                        - 그룹된 데이터의 수치값들을 집계(합계, 평균, 최대, 최소)하는 기능 추가
                        <br />
                        - TGridSchema 의 options 에 group(Array)에 그룹할 필드 정의
                        <br />
                        - TGridSchema 의 group(그룹된 데이터의 가장 위쪽 Row), groupFoot(그룹된 데이터의 가장 아래 Row)
                        설정으로 최대값, 최소값, 평균, 갯수등의 집계 데이터 표시 가능
                        <br />- cell의 aggregate 프로퍼티를 사용하여 집계 데이터 표시
                    </>
                }
            >
                <Sample.Table
                    widths={["w-[25%]", "w-[25%]", "w-auto"]}
                    data={[
                        ["aggregate 구분", "집계 기능", "사용법"],
                        ["SUM", "합계", `binding: "집계할 필드", aggregate: "SUM"`],
                        ["AVERAGE", "평균", `binding: "집계할 필드", aggregate: "AVERAGE"`],
                        ["COUNT", "갯수", `binding: "집계할 필드", aggregate: "COUNT"`],
                        ["MAX", "최대값", `binding: "집계할 필드", aggregate: "MAX"`],
                        ["MIN", "최소값", `binding: "집계할 필드", aggregate: "MIN"`],
                    ]}
                />
                <Sample.Code>{`const GRID_SCHEMA: TGridSchema = {
    options: {
        height: 500,
        index: true,
        // 그룹으로 묶을 데이터의 필드명 정의(배열로 여러개 가능)
        group: ["group"],
    },
    // 그룹으로 묶는 데이터의 제일 상단에 정보 표시하는 Row 설정
    group: [
        { cells: [{ colspan: 2, text: "그룹의 제일 상단 Row" }] },
        //
        { cells: [{ binding: "number1", aggregate: "SUM", align: "right" }] }, // SUM : 합계
        { cells: [{ binding: "number2", aggregate: "AVERAGE", align: "right" }] }, // AVERAGE : 평균
        { cells: [{ binding: "number3", aggregate: "COUNT", align: "right" }] }, // COUNT : 갯수
        { cells: [{ binding: "number4", aggregate: "MAX", align: "right" }] }, // MAX : 최대
        { cells: [{ binding: "number4", aggregate: "MIN", align: "right" }] }, // MIN : 최소
    ],
    // 그룹으로 묶는 데이터의 제일 하단에 정보 표시하는 Row 설정
    groupFoot: [
        { cells: [{ colspan: 2, text: "그룹의 제일 하단 Row" }] },
        { cells: [{ binding: "number1", aggregate: "SUM", align: "right" }] }, // SUM : 합계
        { cells: [{ binding: "number2", aggregate: "AVERAGE", align: "right" }] }, // AVERAGE : 평균
        { cells: [{ binding: "number3", aggregate: "COUNT", align: "right" }] }, // COUNT : 갯수
        { cells: [{ binding: "number4", aggregate: "MAX", align: "right" }] }, // MAX : 최대
        { cells: [{ binding: "number4", aggregate: "MIN", align: "right" }] }, // MIN : 최소
    ],
    head: [
        { cells: [{ binding: "group", width: 200 }] },
        { cells: [{ binding: "subGroup", width: 200 }] },
        { cells: [{ binding: "number1", width: 200 }] },
        { cells: [{ binding: "number2", width: 200 }] },
        { cells: [{ binding: "number3", width: 200 }] },
        { cells: [{ binding: "number4", width: 200 }] },
        { cells: [{ binding: "datetime", width: 200 }] },
    ],
    body: [
        { cells: [{ binding: "group" }] },
        { cells: [{ binding: "subGroup" }] },
        { cells: [{ binding: "number1" }] },
        { cells: [{ binding: "number2" }] },
        { cells: [{ binding: "number3" }] },
        { cells: [{ binding: "number4" }] },
        { cells: [{ binding: "datetime" }] },
    ],
};
`}</Sample.Code>
                <Layout direction="row">
                    <Layout.Right>
                        <Button onClick={() => {}}>Change head schema</Button>
                        <Button onClick={() => {}}>Change body schema</Button>
                    </Layout.Right>
                </Layout>
                <Grid {...grid.rpckItmAppListSum.grid} data={sample({})} />
            </Sample.Section>
            <Sample.Section
                title="5. 그리드 오류검증 기능 추가"
                description={
                    <>
                        - 그리드 데이터의 오류 검증(Schema 기준)
                        <br />
                        - 오류검증 결과를 공통 오류 팝업창에 표시
                        <br />- validate 메소드로 오류 검증
                        <br />- 오류검증 관련 설정 프로퍼티는 useForm 에서 사용하는 프로퍼티와 동일
                    </>
                }
            >
                <Sample.Table
                    widths={["w-[15%]", "w-[25%]", "w-auto"]}
                    data={[
                        ["프로퍼티", "타입", "기본값", "비고"],
                        ["required", "boolean", "false", "항목 필수 여부"],
                        ["min", "any", "", "최소값"],
                        ["max", "any", "", "최대값"],
                        ["minLength", "number", "", "입력값의 최소 길이"],
                        ["maxLength", "number", "", "입력값의 최대 길이"],
                        ["pattern", "정규식", "", "입력값의 정규식 비교, 유효하지 않은 형식"],
                        ["validate", "function", "", "입력값의 함수식 비교, 유효성 검증 실패"],
                    ]}
                />
                <Sample.Code>{`// 그리드 스키마에 오류검증 관련 프로퍼티 설정
export const SG_VALIDATE_LIST: TGridSchema = {
    id: "grid",
    options: {
        radio: true,
        pagination: "in",
        index: true,
        edit: true,
    },
    head: [
        {
            cells: [
                {
                    header: "L_DCLR_NO",
                    binding: "dclrNo",
                    required: true,
                    width: 300,
                },
            ],
        },
        { cells: [{ header: "L_WRHS", binding: "wrhsCd", width: 200 }], id: "wrhsCd" },
        { cells: [{ header: "L_MRN", binding: "mrn", width: 200 }], id: "mrn" },
        {
            cells: [
                {
                    header: "L_MSN",
                    binding: "msn",
                    width: 200,
                },
            ],
        },
        { cells: [{ header: "L_MBL_NO", binding: "mblNo", width: 200 }] },
        { cells: [{ header: "L_GODS_DESC", binding: "godsDesc", width: "3*" }] },
        { cells: [{ header: "L_PCKG_NO", binding: "blPckgNo", width: 200 }] },
        { cells: [{ header: "L_GWGHT", binding: "blGwght", width: 200 }] },
        { cells: [{ header: "L_PRCSS_STAT", binding: "prcssStatCd", width: 200 }] },
        { cells: [{ header: "L_FRST_RGSR_DTM", binding: "frstRgsrDtm", width: 200 }] },
    ],
    body: [
        {
            cells: [{ binding: "dclrNo", required: true, minLength: 5 , pattern: /^[0-9a-zA-Z]([-_.]?[0-9a-zA-Z])*@[0-9a-zA-Z]([-_.]?[0-9a-zA-Z])*.[a-zA-Z]{2,3}$/i,}], // 필수, 최소길이, 이메일 정규식 비교
        },
        {
            cells: [{ binding: "wrhsCd", type: "code", area: "wrhsCd", viewType: "value", required: true }], // 필수
        },
        {
            cells: [{ binding: "mrn", rightText: " KG", required: true }], // 필수
        },

        {
            cells: [{ binding: "msn", required: true }], // 필수
        },
        {
            cells: [{ binding: "mblNo", required: true }], // 필수
        },
        {
            cells: [{ binding: "godsDesc", required: true,  validate: (value: any) => {

                // 값(value)을 비교하여 return true(오류검증 통과) , 이외의 경우 오류검증 실패 처리
                if (value !== "111") return true;

            },  }], // 필수, 함수로 값을 비교하는 오류 검증
        },
        {
            cells: [
                {
                    binding: "blPckgNo",
                    align: "right",
                    type: "number",
                    thousandSeparator: true,
                    required: true,
                    max: 1000,
                },
            ],
        }, // 필수, 최대값 설정
        {
            cells: [
                {
                    binding: "blGwght",
                    align: "right",
                    type: "number",
                    thousandSeparator: true,
                    required: true,
                    min: 200,
                },
            ],
        },  // 필수, 최소값 설정
        {
            cells: [
                {
                    binding: "prcssStatCd",
                    type: "select",
                    required: true,
                    area: "comnCd",
                    comnCd: "OSC0008",
                    viewType: "both",
                },
            ],
        }, // 필수
        {
            cells: [{ binding: "frstRgsrDtm", type: "datetime", required: true }],
        }, // 필수
    ],
};

// 오류검증 버튼 생성

<Button
    onClick={() => {
        // 그리드 오류검증 실행
        const errors = grid.rpckItmAppListValidate.validate();

        // 오류가 존재하는 경우
        if (errors) {
            // 공통 오류팝업창에 해당 오류 내용을 표시
            modal.openModal({
                content: <CommonErrors {...errors} />,
                draggable: true,
                size: "lg",
            });

            return;
        }
    }}
>
    단일 그리드 Validate
</Button>

<Button
    onClick={() => {
        // 다중 그리드 오류검증 실행
        const errors_1 = grid.rpckItmAppListValidate.validate({ name: "그리드명 1" });
        const errors_2 = grid.rpckItmAppListValidate.validate({ name: "그리드명 2" });

        // 오류가 존재하는 경우 or 조건 검사
        if (errors_1 || errors_2) {
            // 공통 오류팝업창에 해당 오류 내용을 표시, 오류 내용 합치기
            modal.openModal({
                content: (
                    <CommonErrors {...{ errors: [...errors_1.errors, ...errors_2.errors] }} />
                ),
                draggable: true,
                size: "lg",
            });

            return;
        }
    }}
>
    다중 그리드 Validate
</Button>

`}</Sample.Code>
                <Layout direction="row">
                    <Layout.Right>
                        <Button
                            onClick={() => {
                                // 그리드 오류검증 실행
                                const errors = grid.rpckItmAppListValidate.validate();

                                // 오류가 존재하는 경우
                                if (errors) {
                                    // 공통 오류팝업창에 해당 오류 내용을 표시
                                    modal.openModal({
                                        content: <CommonErrors {...errors} />,
                                        draggable: true,
                                        size: "lg",
                                    });

                                    return;
                                }
                            }}
                        >
                            단일 그리드 Validate
                        </Button>

                        <Button
                            onClick={() => {
                                // 다중 그리드 오류검증 실행
                                const errors_1 = grid.rpckItmAppListValidate.validate({ name: "그리드명 1" });
                                const errors_2 = grid.rpckItmAppListValidate.validate({ name: "그리드명 2" });

                                // 오류가 존재하는 경우 or 조건 검사
                                if (errors_1 || errors_2) {
                                    // 공통 오류팝업창에 해당 오류 내용을 표시, 오류 내용 합치기
                                    modal.openModal({
                                        content: (
                                            <CommonErrors {...{ errors: [...errors_1.errors, ...errors_2.errors] }} />
                                        ),
                                        draggable: true,
                                        size: "lg",
                                    });

                                    return;
                                }
                            }}
                        >
                            다중 그리드 Validate
                        </Button>
                    </Layout.Right>
                </Layout>
                <Grid {...grid.rpckItmAppListValidate.grid} data={validateSample({ totalElements: 10 })} />
            </Sample.Section>
        </Sample>
    );
};
